'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import type { CompassItem } from './BastionCompass';
import {
  collectAnchorIds,
  getSectionScrollTop,
  scrollToSectionAnchor,
} from './stockSectionUtils';

export const STOCK_MOBILE_TAB_BP = '(max-width: 999px)';

interface StockSectionContextValue {
  activeSectionId: string;
  /**
   * Layout-only: true below 1000px for the sticky horizontal compass chrome.
   * Does NOT unmount sections or disable scroll-spy (Simply Wall St style).
   */
  isTabMode: boolean;
  sectionIds: string[];
  /** When true, lazy chart sections should mount fully (compass jump). */
  eagerMountSections: boolean;
  selectSection: (id: string) => void;
  navigateToSection: (id: string) => void;
  isSectionVisible: (id: string) => boolean;
}

const StockSectionContext = createContext<StockSectionContextValue | null>(null);

const noop = () => {};

const fallbackValue: StockSectionContextValue = {
  activeSectionId: '',
  isTabMode: false,
  sectionIds: [],
  eagerMountSections: false,
  selectSection: noop,
  navigateToSection: noop,
  isSectionVisible: () => true,
};

export function useStockSection() {
  return useContext(StockSectionContext) ?? fallbackValue;
}

function flattenSectionIds(items: CompassItem[]): string[] {
  return items.map((it) => it.id);
}

/** Sticky topnav + section-bar clearance on mobile (measured live). */
function readMobileScrollOffset(fallback: number): number {
  if (typeof document === 'undefined') return fallback;
  const stack = document.querySelector('.stock-sidebar-stack') as HTMLElement | null;
  if (!stack) return Math.max(fallback, 120);
  const bottom = stack.getBoundingClientRect().bottom;
  if (bottom > 40) return Math.ceil(bottom) + 8;
  return Math.max(fallback, 120);
}

export function StockSectionProvider({
  items,
  children,
  headerOffset = 80,
}: {
  items: CompassItem[];
  children: React.ReactNode;
  headerOffset?: number;
}) {
  const sectionIds = useMemo(() => flattenSectionIds(items), [items]);
  const anchorIds = useMemo(() => collectAnchorIds(items), [items]);
  const [activeSectionId, setActiveSectionId] = useState(() => items[0]?.id ?? '');
  const [eagerMountSections, setEagerMountSections] = useState(false);
  /** Mobile horizontal compass layout only — not exclusive tab mounting. */
  const [isTabMode, setIsTabMode] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(STOCK_MOBILE_TAB_BP).matches,
  );
  const pendingUserNavRef = useRef<{ id: string; until: number } | null>(null);
  const pendingScrollRef = useRef<{ id: string; navGen: number } | null>(null);
  const navGenRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(STOCK_MOBILE_TAB_BP);
    const update = () => setIsTabMode(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (sectionIds.length === 0) return;
    setActiveSectionId((prev) => {
      if (prev && (sectionIds.includes(prev) || anchorIds.includes(prev))) return prev;
      return sectionIds[0];
    });
  }, [sectionIds, anchorIds]);

  const effectiveOffset = useCallback(() => {
    return isTabMode ? readMobileScrollOffset(headerOffset) : headerOffset;
  }, [headerOffset, isTabMode]);

  const selectSection = useCallback((id: string) => {
    setActiveSectionId(id);
  }, []);

  const navigateToSection = useCallback((id: string) => {
    // Expand lazy chart skeletons before measuring scroll — otherwise the
    // jump lands short and the target drifts down as dashboards mount.
    setEagerMountSections(true);
    navGenRef.current += 1;
    pendingUserNavRef.current = { id, until: Date.now() + 1400 };
    pendingScrollRef.current = { id, navGen: navGenRef.current };
    setActiveSectionId(id);
  }, []);

  /* After eager mounts commit, jump once and only re-snap on layout growth —
     never fight the user's own wheel / touch / keyboard scroll. */
  useEffect(() => {
    const pending = pendingScrollRef.current;
    if (!pending) return;

    const navGen = pending.navGen;
    const targetId = pending.id;
    let cancelled = false;
    let userTookOver = false;
    let settleUntil = 0;

    const release = () => {
      if (pendingScrollRef.current?.navGen === navGen) {
        pendingScrollRef.current = null;
      }
      // Let scroll-spy resume immediately after the user scrolls away.
      if (pendingUserNavRef.current?.id === targetId) {
        pendingUserNavRef.current = null;
      }
    };

    const isActive = () =>
      !cancelled && !userTookOver && navGenRef.current === navGen;

    const snap = () => {
      if (!isActive()) return;
      scrollToSectionAnchor(targetId, effectiveOffset(), 'auto');
    };

    const correctIfDrifted = () => {
      if (!isActive()) return;
      if (Date.now() > settleUntil) {
        release();
        return;
      }
      const top = getSectionScrollTop(targetId, effectiveOffset());
      if (top == null) return;
      if (Math.abs(window.scrollY - top) > 24) {
        window.scrollTo({ top, behavior: 'auto' });
      }
    };

    const onUserScrollIntent = () => {
      if (!isActive()) return;
      userTookOver = true;
      release();
    };

    const onKeyScrollIntent = (e: KeyboardEvent) => {
      const key = e.key;
      if (
        key === 'ArrowUp' ||
        key === 'ArrowDown' ||
        key === 'PageUp' ||
        key === 'PageDown' ||
        key === 'Home' ||
        key === 'End' ||
        key === ' ' ||
        key === 'Spacebar'
      ) {
        onUserScrollIntent();
      }
    };

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!isActive()) return;
        snap();
        settleUntil = Date.now() + 1200;
        // One follow-up after paint in case charts grew in the same frame.
        requestAnimationFrame(() => {
          if (!isActive()) return;
          correctIfDrifted();
        });
      });
    });

    window.addEventListener('wheel', onUserScrollIntent, { passive: true, capture: true });
    window.addEventListener('touchmove', onUserScrollIntent, { passive: true, capture: true });
    window.addEventListener('keydown', onKeyScrollIntent, { capture: true });

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => correctIfDrifted());
      ro.observe(document.documentElement);
      const targetEl = document.getElementById(targetId);
      if (targetEl) ro.observe(targetEl);
    }

    const stopId = window.setTimeout(() => {
      if (!isActive()) return;
      correctIfDrifted();
      release();
    }, 1400);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(stopId);
      window.removeEventListener('wheel', onUserScrollIntent, true);
      window.removeEventListener('touchmove', onUserScrollIntent, true);
      window.removeEventListener('keydown', onKeyScrollIntent, true);
      ro?.disconnect();
    };
  }, [headerOffset, isTabMode, effectiveOffset, eagerMountSections, activeSectionId]);

  /* Scroll spy — compass highlight tracks the nearest section (desktop + mobile). */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (anchorIds.length === 0) return;

    const update = () => {
      const pendingNav = pendingUserNavRef.current;
      if (pendingNav && Date.now() < pendingNav.until) {
        setActiveSectionId(pendingNav.id);
        return;
      }

      const offset = effectiveOffset();
      // A section counts as current once its top passes a third of the way
      // down the viewport (not just under the sticky header): chapters open
      // with a tall break, so the old line lit the next chapter far too late.
      const triggerY = Math.max(offset + 24, window.innerHeight * 0.33);
      let bestId = '';
      let bestTop = -Infinity;
      for (const id of anchorIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= triggerY && top > bestTop) {
          bestTop = top;
          bestId = id;
        }
      }
      // Above the first section (page top / profile), nothing has crossed the
      // trigger line — keep the first chapter active instead of a stale tab.
      setActiveSectionId(bestId || anchorIds[0]);
    };

    update();
    const onScroll = () => requestAnimationFrame(update);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [anchorIds, effectiveOffset]);

  const isSectionVisible = useCallback((_sectionId: string) => true, []);

  const value = useMemo(
    () => ({
      activeSectionId,
      isTabMode,
      sectionIds,
      eagerMountSections,
      selectSection,
      navigateToSection,
      isSectionVisible,
    }),
    [
      activeSectionId,
      isTabMode,
      sectionIds,
      eagerMountSections,
      selectSection,
      navigateToSection,
      isSectionVisible,
    ],
  );

  return (
    <StockSectionContext.Provider value={value}>
      {children}
    </StockSectionContext.Provider>
  );
}
